import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("cs_token") || "");
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("cs_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [profile, setProfileState] = useState(() => {
    const raw = localStorage.getItem("cs_profile");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  function setProfile(next) {
    setProfileState(next);
    if (next) localStorage.setItem("cs_profile", JSON.stringify(next));
    else localStorage.removeItem("cs_profile");
  }

  useEffect(() => {
    if (!token) return;
    api("/api/auth/me", { token })
      .then((me) => {
        setUser(me);
        localStorage.setItem("cs_user", JSON.stringify(me));
      })
      .catch(() => {
        setToken("");
        setUser(null);
        setProfile(null);
        localStorage.removeItem("cs_token");
        localStorage.removeItem("cs_user");
        localStorage.removeItem("cs_profile");
      });
  }, [token]);

  useEffect(() => {
    if (!token || !user) return undefined;
    let cancelled = false;
    const currentId = (() => {
      try {
        const raw = localStorage.getItem("cs_profile");
        return raw ? JSON.parse(raw)?.id : null;
      } catch {
        return null;
      }
    })();
    api("/api/profiles", { token })
      .then((data) => {
        if (cancelled) return;
        const list = data.profiles || [];
        if (!list.length) {
          setProfile(null);
          return;
        }
        const still = currentId ? list.find((p) => p.id === currentId) : null;
        if (still) setProfile(still);
        else if (list.length === 1) setProfile(list[0]);
        else setProfile(null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, user?.id]);

  const value = useMemo(
    () => ({
      token,
      user,
      profile,
      loading,
      isAdmin: user?.role === "admin",
      isKidsProfile: Boolean(profile?.is_kids),
      setProfile,
      setUser: (next) => {
        setUser(next);
        if (next) localStorage.setItem("cs_user", JSON.stringify(next));
        else localStorage.removeItem("cs_user");
      },
      async login(username, password) {
        setLoading(true);
        try {
          const data = await api("/api/auth/login", {
            method: "POST",
            body: { username, password },
          });
          setToken(data.access_token);
          setUser(data.user);
          localStorage.setItem("cs_token", data.access_token);
          localStorage.setItem("cs_user", JSON.stringify(data.user));
          setProfile(null);
          return data.user;
        } finally {
          setLoading(false);
        }
      },
      logout() {
        setToken("");
        setUser(null);
        setProfile(null);
        localStorage.removeItem("cs_token");
        localStorage.removeItem("cs_user");
        localStorage.removeItem("cs_profile");
      },
    }),
    [token, user, profile, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
